import notesService from '../services/notesService';

const state = {
  currentNote: null,
  loading: false,
  error: null,
};

const getters = {
  currentNote: (state) => state.currentNote,
  isLoading: (state) => state.loading,
  hasError: (state) => !!state.error,
  error: (state) => state.error,
};

const mutations = {
  setCurrentNote(state, note) {
    state.currentNote = note;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  clearError(state) {
    state.error = null;
  },
};

const actions = {
  /**
   * Load a note by UID for editing
   * @param {Object} context - Vuex context
   * @param {string} noteUid - The note UID to load
   * @returns {Promise<Object>} The loaded note
   */
  async loadNote({ commit }, noteUid) {
    commit('setLoading', true);
    commit('clearError');
    try {
      const note = await notesService.get(noteUid);
      commit('setCurrentNote', note);
      return note;
    } catch (err) {
      commit('setError', err);
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Save a new note
   * @param {Object} context - Vuex context
   * @param {Object} noteData - The note data to save
   * @param {string} noteData.matter_uid - Matter UID (required)
   * @param {string} [noteData.title] - Note title
   * @param {string} [noteData.content] - Note content
   * @param {string} [noteData.entity_uid] - Related entity UID
   * @param {string} [noteData.entity_type] - Related entity type
   * @returns {Promise<Object>} The saved note
   */
  async saveNote({ commit }, noteData) {
    commit('setLoading', true);
    commit('clearError');
    try {
      const savedNote = await notesService.add(noteData);
      return savedNote;
    } catch (err) {
      commit('setError', err);
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Update an existing note
   * @param {Object} context - Vuex context
   * @param {Object} params - Update parameters
   * @param {string} params.noteUid - The note UID to update
   * @param {Object} params.noteData - The note data to update
   * @param {string} [params.noteData.title] - Note title
   * @param {string} [params.noteData.content] - Note content
   * @returns {Promise<Object>} The updated note
   */
  async updateNote({ commit }, { noteUid, noteData }) {
    commit('setLoading', true);
    commit('clearError');
    try {
      const updatedNote = await notesService.update(noteUid, noteData);
      commit('setCurrentNote', updatedNote);
      return updatedNote;
    } catch (err) {
      commit('setError', err);
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Clear the current note from state
   */
  clearCurrentNote({ commit }) {
    commit('setCurrentNote', null);
    commit('clearError');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
